import React from "react";

import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import {SupportAgent} from "@vedmarket/ui-kit";
import cx from "classnames";

import {COLORS} from "../../../../../../../config/constants";
import SupportRequestAttachments from "./components/attachments";

import './support-request-thread.scss'

type Props = {
  messages: SupportRequestMessage[]
}

const getLatestMessageId = (messages: SupportRequestMessage[]) => (
  messages.reduce<SupportRequestMessage | undefined>((latestMessage, message) => {
    if (!latestMessage) {
      return message;
    }

    if (message.createdAt && latestMessage.createdAt) {
      return message.createdAt > latestMessage.createdAt ? message : latestMessage;
    }

    return message;
  }, undefined)?.id
)

const getMessageVariant = (
  message: SupportRequestMessage,
  latestMessageId?: string,
): SupportRequestMessageVariant => {
  if (message.author === 'support') {
    return 'inline';
  }

  if (message.id === latestMessageId) {
    return 'highlighted_card';
  }

  return 'card';
}

const SupportRequestThread = ({messages}: Props) => {

  const latestMessageId = getLatestMessageId(messages);

  const threadMessages = messages.reduce<React.ReactNode[]>((items, message) => {

    const messageVariant = getMessageVariant(message, latestMessageId);

    const isCard = messageVariant !== 'inline';
    const isHighlighted = messageVariant === 'highlighted_card';
    const isOutlinedCard = messageVariant === 'card';
    const isSupportInline = message.author === 'support' && !isCard;

    const inlineMessageColor = isSupportInline ? COLORS.MEDIUM_GRAY : COLORS.BLACK;
    const inlineMessageLineHeight = isSupportInline ? '20px' : '28px';

    if (!isCard) {
      items.push(
        <div key={message.id} className='support-request-thread__inline-message'>
          {isSupportInline && <SupportAgent className='support-request-thread__inline-icon' />}
          <div className='support-request-thread__inline-text'>
            {message.text.map((text) => (
              <Typography
                key={text}
                variant='body2'
                sx={{color: inlineMessageColor, letterSpacing: 0, lineHeight: inlineMessageLineHeight}}
              >
                {text}
              </Typography>
            ))}
            <SupportRequestAttachments attachments={message.attachments} />
          </div>
        </div>
      );

      return items;
    }

    items.push(
      <Card
        key={message.id}
        className={cx('support-request-thread__card', {
          'support-request-thread__card_highlighted': isHighlighted,
          'support-request-thread__card_outlined': isOutlinedCard,
        })}
      >
        <div className={cx(
          'support-request-thread__card-content',
          {'support-request-thread__card-content_outlined': isOutlinedCard}
        )}>
          {message.title && (
            <div className='support-request-thread__card-header'>
              <Typography
                variant='subtitle1'
                sx={{color: COLORS.BLACK, fontWeight: 500, letterSpacing: 0, lineHeight: '24px'}}
              >
                {message.title}
              </Typography>
            </div>
          )}
          <div className='support-request-thread__card-text'>
            {message.text.map((text) => (
              <Typography
                key={text}
                variant='body1'
                sx={{color: COLORS.BLACK_DARK, letterSpacing: 0, lineHeight: '24px'}}
              >
                {text}
              </Typography>
            ))}
            <SupportRequestAttachments attachments={message.attachments} />
          </div>
        </div>
      </Card>
    );

    return items;
  }, []);

  return (
    <div className='support-request-thread'>
      {threadMessages}
    </div>
  );
};

export default SupportRequestThread;
